import React, { useState, useEffect } from 'react';
import { DatePickerComponent, ChangeEventArgs } from '@syncfusion/ej2-react-calendars';
import { useFormContext } from 'react-hook-form';
import { TooltipComponent } from '@syncfusion/ej2-react-popups';
import { Modal } from 'react-bootstrap';
import get from 'lodash/get';

interface IInputProps {
  name: string;
  id: string;
  label?: string;
  placeholder?: string;
  getDayWeek?: (date: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  req?: string;
  disableDate?: boolean;
  modal?: string;
  xl?: number;
  md?: number;
  col?: number;
  /** 𝗡𝗨𝗘𝗩𝗢 → valor por defecto para el selector.  Solo se aplicará si contiene una fecha válida */
  defaultValue?: Date | null | '';
}

/**
 * Devuelve true si value es una instancia Date válida (no NaN)
 */
const isValidDate = (value: unknown): value is Date =>
  value instanceof Date && !isNaN(value.getTime());

const FormInputFecha: React.FC<IInputProps> = ({
  name,
  id,
  label,
  getDayWeek,
  minDate,
  maxDate,
  req,
  disableDate,
  modal,
  xl = 12,
  md = 12,
  col = 12,
  defaultValue,
  placeholder,
}) => {
  const {
    register,
    formState: { errors },
    setValue,
  } = useFormContext();

  // 1️⃣ Establece el valor inicial sólo una vez, comprobando que sea una fecha válida.
  const initialDate = isValidDate(defaultValue) ? defaultValue : null;
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);

  // 2️⃣ Sincroniza el estado si defaultValue llega/varía después del primer render (por ejemplo, llamadas async)
  useEffect(() => {
    if (isValidDate(defaultValue) && !selectedDate) {
      setSelectedDate(defaultValue);
    }
  }, [defaultValue, selectedDate]);

  const handleDateChange = (args: ChangeEventArgs) => {
    const date = args.value as Date | null;
    if (date) {
      date.setHours(12, 0, 0, 0);
      setSelectedDate(date);
      setValue(name, date, { shouldValidate: true, shouldDirty: true });
      if (getDayWeek) {
        getDayWeek(date);
      }
    } else {
      setSelectedDate(null);
      setValue(name, null, { shouldValidate: true, shouldDirty: true });
    }
  };

  const errorMessage = get(errors, name)?.message as string | undefined;

  /////// MODAL ///////
  const [show, setShow] = useState(false);
  const handleShow = () => setShow(true);
  const handleClose = () => setShow(false);

  return (
    <div className={`col-xl-${xl} col-md-${md} col-${col}`}>
      <div className={`input-block ${id} ${errorMessage ? 'error' : ''}`}>
        {(label || modal) && (
          <div className="d-flex align-items-center gap-1 mb-1">
            {label && (
              <label htmlFor={id} className="input-block__label mb-0">
                {label}
              </label>
            )}
            {modal && (
              <TooltipComponent content="Más información">
                <div onClick={handleShow} className="information-icon-orange p-0" />
              </TooltipComponent>
            )}
          </div>
        )}

        <DatePickerComponent
          format="dd-MM-yyyy"
          name={name}
          id={id}
          change={handleDateChange}
          locale=''
          value={selectedDate ?? undefined}
          firstDayOfWeek={1}
          {...register(name, { required: req })}
          min={minDate}
          max={maxDate}
          disabled={disableDate}
          placeholder={placeholder}
        />

        {errorMessage && <p className="error m-0">{errorMessage}</p>}

        <Modal show={show} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>{label}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{modal}</Modal.Body>
        </Modal>
      </div>
    </div>
  );
};

export default FormInputFecha;

